import { useState } from "react";
import { Coins, DollarSign, History } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type {
  FortAgentAifVestingPayoutRecordItem,
  FortAgentStaticPayoutRecordItem,
} from "@/api/types";
import { C } from "./homeTheme";
import { useTranslation } from "react-i18next";
import { getTokenName } from "@/config/common";
import { CCC_AIF_ADDRESS, USDT_ADDRESS } from "@/config/env";
import { formatEtherWithDecimals, formatUsdPrefix } from "@/lib/utils";
import { useGetFortAgentStaticPayoutRecords } from "@/hooks/api/useGetFortAgentStaticPayoutRecords";
import { useGetFortAgentAifVestingPayoutRecords } from "@/hooks/api/useGetFortAgentAifVestingPayoutRecords";
import {
  formatFortAgentPayoutTeamLevel,
  formatFortAgentStaticRateBpms,
} from "./fortAgentPayoutUtils";

function IncomeHistoryEmpty() {
  const { t } = useTranslation();
  return (
    <div className="text-center py-8">
      <History size={24} className="mx-auto mb-2" style={{ color: C.dim }} />
      <p className="text-[11px]" style={{ color: C.dim }}>
        {t("homePage.incomeHistoryDialog.empty")}
      </p>
    </div>
  );
}

function useIncomeScrollLoadMore(
  hasNextPage: boolean | undefined,
  isFetchingNextPage: boolean,
  fetchNextPage: () => void
) {
  return (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    if (
      el.scrollHeight - el.scrollTop - el.clientHeight < 48 &&
      hasNextPage &&
      !isFetchingNextPage
    ) {
      void fetchNextPage();
    }
  };
}

export function IncomeHistoryDialog({
  open,
  onClose,
  agentId = 0,
}: {
  open: boolean;
  onClose: () => void;
  agentId?: number;
}) {
  const { t } = useTranslation();
  const [tab, setTab] = useState<"usdt" | "aif">("usdt");
  const resolvedAgentId = agentId ?? 0;

  const {
    data: staticData,
    isLoading: staticLoading,
    isError: staticError,
    isFetchingNextPage: staticFetchingNext,
    hasNextPage: staticHasNext,
    fetchNextPage: fetchNextStatic,
  } = useGetFortAgentStaticPayoutRecords(
    resolvedAgentId,
    open && tab === "usdt"
  );

  const {
    data: aifData,
    isLoading: aifLoading,
    isError: aifError,
    isFetchingNextPage: aifFetchingNext,
    hasNextPage: aifHasNext,
    fetchNextPage: fetchNextAif,
  } = useGetFortAgentAifVestingPayoutRecords(
    resolvedAgentId,
    open && tab === "aif"
  );

  const staticRecords =
    staticData?.pages.flatMap(page => page.records ?? []) ?? [];
  const aifRecords = aifData?.pages.flatMap(page => page.records ?? []) ?? [];

  const loading = tab === "usdt" ? staticLoading : aifLoading;
  const isError = tab === "usdt" ? staticError : aifError;
  const isFetchingNextPage =
    tab === "usdt" ? staticFetchingNext : aifFetchingNext;
  const hasNextPage = tab === "usdt" ? staticHasNext : aifHasNext;
  const fetchNextPage = tab === "usdt" ? fetchNextStatic : fetchNextAif;
  const records = tab === "usdt" ? staticRecords : aifRecords;
  const isEmpty = !loading && !isError && records.length === 0;

  const handleScroll = useIncomeScrollLoadMore(
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage
  );

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        className="max-w-sm mx-auto"
        style={{
          background: C.bg,
          border: `1px solid ${C.border}`,
          color: C.text,
        }}
      >
        <DialogHeader>
          <DialogTitle style={{ color: C.primary }}>
            {t("homePage.incomeHistoryDialog.title")}
          </DialogTitle>
        </DialogHeader>

        <div
          className="flex rounded-lg overflow-hidden mb-3"
          style={{ background: C.card2, border: `1px solid ${C.border}` }}
        >
          <button
            type="button"
            className="flex-1 py-2 text-xs font-semibold transition-all flex items-center justify-center gap-1.5"
            style={{
              background: tab === "usdt" ? `${C.primary}12` : "transparent",
              color: tab === "usdt" ? C.primary : C.dim,
            }}
            onClick={() => setTab("usdt")}
          >
            <DollarSign size={12} />
            {t("homePage.incomeHistoryDialog.usdtTab", {
              tokenNameUsdt: getTokenName(USDT_ADDRESS),
            })}
          </button>
          <button
            type="button"
            className="flex-1 py-2 text-xs font-semibold transition-all flex items-center justify-center gap-1.5"
            style={{
              background: tab === "aif" ? `${C.amber}12` : "transparent",
              color: tab === "aif" ? C.amber : C.dim,
            }}
            onClick={() => setTab("aif")}
          >
            <Coins size={12} />
            {t("homePage.incomeHistoryDialog.aifTab", {
              tokenNameAif: getTokenName(CCC_AIF_ADDRESS),
            })}
          </button>
        </div>

        <div
          className="space-y-2 max-h-80 overflow-y-auto pr-1"
          onScroll={handleScroll}
        >
          {loading ? (
            <p className="text-center text-xs py-8" style={{ color: C.dim }}>
              {t("homePage.incomeHistoryDialog.loading")}
            </p>
          ) : isError ? (
            <p className="text-center text-xs py-8" style={{ color: C.red }}>
              {t("homePage.incomeHistoryDialog.loadFailed")}
            </p>
          ) : isEmpty ? (
            <IncomeHistoryEmpty />
          ) : tab === "usdt" ? (
            staticRecords.map(item => (
              <StaticPayoutRow key={item.id} item={item} />
            ))
          ) : (
            aifRecords.map(item => <AifPayoutRow key={item.id} item={item} />)
          )}
          {isFetchingNextPage && (
            <p
              className="text-center text-[10px] py-2"
              style={{ color: C.dim }}
            >
              {t("homePage.incomeHistoryDialog.loadingMore")}
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function StaticPayoutRow({ item }: { item: FortAgentStaticPayoutRecordItem }) {
  const { t } = useTranslation();
  return (
    <div
      className="flex justify-between items-center p-3 rounded-lg"
      style={{ background: C.card2, border: `1px solid ${C.border}` }}
    >
      <div>
        <div className="text-xs font-medium">
          {t("homePage.incomeHistoryDialog.staticIncome", {
            rate: formatFortAgentStaticRateBpms(item.rateBpms / 100),
          })}
        </div>
        <div className="text-[10px]" style={{ color: C.dim }}>
          {t("homePage.incomeHistoryDialog.dateLevel", {
            date: item.bizDate,
            level: formatFortAgentPayoutTeamLevel(item.teamLevel),
          })}
        </div>
      </div>
      <div className="mono-num font-bold text-sm" style={{ color: C.green }}>
        +{formatUsdPrefix(formatEtherWithDecimals(item.amountUsdt, 2))}
      </div>
    </div>
  );
}

function AifPayoutRow({ item }: { item: FortAgentAifVestingPayoutRecordItem }) {
  const { t } = useTranslation();
  return (
    <div
      className="flex justify-between items-center p-3 rounded-lg"
      style={{ background: C.card2, border: `1px solid ${C.border}` }}
    >
      <div>
        <div className="text-xs font-medium">
          {t("homePage.incomeHistoryDialog.aifDistribution", {
            tokenNameAif: getTokenName(CCC_AIF_ADDRESS),
          })}
        </div>
        <div className="text-[10px]" style={{ color: C.dim }}>
          {t("homePage.incomeHistoryDialog.aifDateDetail", {
            date: item.bizDate,
            usdValue: formatUsdPrefix(
              formatEtherWithDecimals(item.valueUsdt, 2)
            ),
            price: formatUsdPrefix(item.aifPriceUsdt),
          })}
        </div>
      </div>
      <div className="mono-num font-bold text-sm" style={{ color: C.amber }}>
        +${formatEtherWithDecimals(item.valueUsdt, 2)}{" "}
        {getTokenName(CCC_AIF_ADDRESS)}
      </div>
    </div>
  );
}
